import React, { useEffect, useMemo, useState } from "react";
import {
  IonCardContent,
  IonContent,
  IonPage,
  useIonRouter,
} from "@ionic/react";
import Header from "../../../components/Header";
import { fetchDocumentByType } from "../../../services/documents";
import { Preferences } from "@capacitor/preferences";

export interface PrivacyPolicyDocument {
  id: number;
  type: string;
  version: string;
  effective_date: string;
  last_updated: string;
  title: string;
  content_markdown: string;
  content_html: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export const PRIVACY_POLICY_KEY = "privacyPolicy";

/**
 * ✅ Reusable content renderer (exported separately)
 * Can be used in any page or modal that just needs to display the policy HTML
 */
export const PrivacyPolicyContent: React.FC<{
  html?: string;
  isLoading?: boolean;
}> = ({ html, isLoading }) => {
  const renderedHtml = useMemo(() => {
    if (isLoading) {
      return <p style={{ textAlign: "center" }}>Loading privacy policy...</p>;
    }
    if (!html) {
      return (
        <p style={{ textAlign: "center" }}>
          Privacy policy not available. Please check your connection.
        </p>
      );
    }
    return (
      <div
        className="overflow-auto h-full" style={{ lineHeight: "1.6" , fontFamily: 'Figtree, sans-serif !important'}}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }, [html, isLoading]);

  return <>{renderedHtml}</>;
};

/**
 * 📄 Page Component (handles fetching, caching, and navigation)
 */
const PrivacyPolicy: React.FC = () => {
  const router = useIonRouter();
  const [privacyPolicy, setPrivacyPolicy] =
    useState<PrivacyPolicyDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleBack = () => router.goBack();

  useEffect(() => {
    const loadPrivacyPolicy = async () => {
      try {
        // 1️⃣ Try loading from local storage first
        const localResult = await Preferences.get({ key: PRIVACY_POLICY_KEY });
        let localPolicy: PrivacyPolicyDocument | null = null;

        if (localResult.value) {
          localPolicy = JSON.parse(localResult.value);
          setPrivacyPolicy(localPolicy);
          setIsLoading(false); // show cached version immediately
        }

        // 2️⃣ Always check remote for updates
        const remotePolicy = await fetchDocumentByType("privacy");

        // 3️⃣ Update cache only if newer or missing
        if (
          !localPolicy ||
          localPolicy.updated_at !== remotePolicy.updated_at
        ) {
          await Preferences.set({
            key: PRIVACY_POLICY_KEY,
            value: JSON.stringify(remotePolicy),
          });
          setPrivacyPolicy(remotePolicy);
        }
      } catch (error) {
        console.error("Failed to load privacy policy:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPrivacyPolicy();
  }, []);

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle
        showUserIcon={false}
        pageTitle="Privacy Policy"
        showBackButton
        onBackButtonClick={handleBack}
      />

      <IonContent className="ion-padding" style={{ "--background": "#F7F5FA" }}>
        <IonCardContent>
          <PrivacyPolicyContent
            html={privacyPolicy?.content_html}
            isLoading={isLoading}
          />
        </IonCardContent>
      </IonContent>
    </IonPage>
  );
};

export default PrivacyPolicy;
